export function hasHtml(value: string | null | undefined): boolean {
    return !!value && /<[^>]+>/.test(value);
}

export function convertMarkdownToHtml(markdown: string): string {
    if (!markdown?.trim()) {
        return '<p class="preview-placeholder">Nothing to preview yet.</p>';
    }

    const escaped = escapeHtml(markdown);
    const lines = escaped.split(/\r?\n/);
    const html: string[] = [];
    let inUl = false;
    let inOl = false;

    const closeLists = () => {
        if (inUl) {
            html.push('</ul>');
            inUl = false;
        }
        if (inOl) {
            html.push('</ol>');
            inOl = false;
        }
    };

    for (const rawLine of lines) {
        const line = rawLine.trim();

        if (!line) {
            closeLists();
            continue;
        }

        if (/^###\s+/.test(line)) {
            closeLists();
            html.push(`<h3>${parseInlineMarkdown(line.replace(/^###\s+/, ''))}</h3>`);
            continue;
        }

        if (/^##\s+/.test(line)) {
            closeLists();
            html.push(`<h2>${parseInlineMarkdown(line.replace(/^##\s+/, ''))}</h2>`);
            continue;
        }

        if (/^#\s+/.test(line)) {
            closeLists();
            html.push(`<h1>${parseInlineMarkdown(line.replace(/^#\s+/, ''))}</h1>`);
            continue;
        }

        if (/^[-*]\s+/.test(line)) {
            if (!inUl) {
                closeLists();
                html.push('<ul>');
                inUl = true;
            }
            html.push(`<li>${parseInlineMarkdown(line.replace(/^[-*]\s+/, ''))}</li>`);
            continue;
        }

        if (/^\d+\.\s+/.test(line)) {
            if (!inOl) {
                closeLists();
                html.push('<ol>');
                inOl = true;
            }
            html.push(`<li>${parseInlineMarkdown(line.replace(/^\d+\.\s+/, ''))}</li>`);
            continue;
        }

        closeLists();
        html.push(`<p>${parseInlineMarkdown(line)}</p>`);
    }

    closeLists();
    return html.join('');
}

export function normalizeDescriptionForEditor(value: string | null | undefined): string {
    if (!value) {
        return '';
    }

    if (!hasHtml(value)) {
        return value;
    }

    return value
        .replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_match, content) => convertHtmlListToMarkdown(content, true))
        .replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_match, content) => convertHtmlListToMarkdown(content, false))
        .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
        .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n')
        .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n')
        .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
        .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
        .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
        .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
        .replace(/<a[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>\s*<p>/gi, '\n\n')
        .replace(/<p[^>]*>/gi, '')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<\/?(ul|ol)>/gi, '')
        .replace(/<[^>]*>/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function parseInlineMarkdown(value: string): string {
    return value
        .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>');
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function convertHtmlListToMarkdown(content: string, isOrdered: boolean): string {
    const items = Array.from(content.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)).map((match) =>
        match[1]
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
            .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
            .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
            .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
            .replace(/<a[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
            .replace(/<[^>]*>/g, '')
            .trim(),
    );

    if (!items.length) {
        return '';
    }

    return `${items
        .map((item, index) => (isOrdered ? `${index + 1}. ${item}` : `- ${item}`))
        .join('\n')}\n\n`;
}
